import { format, isAfter, isBefore, parse } from "date-fns";
import { computed, ref, toRefs, watch } from "vue";

import {
  fromCurrentToGivenTimezone,
  getTimezone,
  swapTimezones,
} from "shared/helpers/date";
import type { Nullable } from "shared/types";

type TimeUnit = "h" | "m" | "s";

interface TimeOption {
  disabled: boolean;
  label: number | string;
  value: number;
}

interface UseTimeContext {
  emit: (event: "update:modelValue", value: Nullable<Date>) => void;
}

interface UseTimeProps {
  interval: number;
  maxTime: Nullable<Date>;
  minTime: Nullable<Date>;
  minuteStep: number;
  modelValue: Date;
  showSeconds: boolean;
  timezone: string;
}

export default function useTime(props: UseTimeProps, context: UseTimeContext) {
  const {
    interval,
    maxTime,
    minTime,
    minuteStep,
    modelValue,
    showSeconds,
    timezone,
  } = toRefs(props);

  const error = ref(false);
  const pendingLocalTime = ref<Nullable<Date>>(null);
  const previousLocalTime = ref<Date>(modelValue.value);
  const hourOptions = ref<TimeOption[]>([]);
  const minuteOptions = ref<TimeOption[]>([]);
  const secondOptions = ref<TimeOption[]>([]);

  const localTime = computed<Date>(() => {
    if (timezone.value) {
      return fromCurrentToGivenTimezone(modelValue.value, timezone.value);
    }

    return modelValue.value;
  });

  const newLocalTime = computed<Date>(
    () => pendingLocalTime.value || localTime.value
  );

  const localMaxTime = computed<Nullable<Date>>(() => {
    if (!maxTime.value) {
      return null;
    }

    if (timezone.value) {
      return fromCurrentToGivenTimezone(maxTime.value, timezone.value);
    }

    return maxTime.value;
  });

  const localMinTime = computed<Nullable<Date>>(() => {
    if (!minTime.value) {
      return null;
    }

    if (timezone.value) {
      return fromCurrentToGivenTimezone(minTime.value, timezone.value);
    }

    return minTime.value;
  });

  const formatString = computed<string>(() =>
    showSeconds.value ? "HH:mm:ss" : "HH:mm"
  );

  function setDate(date: Date) {
    let invalid = false;
    let dateError = false;

    if (date.toString() === "Invalid Date") {
      invalid = true;
      dateError = true;
    }

    if (localMinTime.value && isBefore(date, localMinTime.value)) {
      dateError = true;
    }

    if (localMaxTime.value && isAfter(date, localMaxTime.value)) {
      dateError = true;
    }

    if (minuteStep.value > 1) {
      const minutes = Number(format(date, "m"));

      if (minutes % minuteStep.value !== 0) {
        dateError = true;
      }
    }

    error.value = dateError;

    if (!invalid) {
      pendingLocalTime.value = timezone.value
        ? swapTimezones(date, timezone.value, getTimezone())
        : date;
    }

    if (!dateError) {
      context.emit("update:modelValue", pendingLocalTime.value);
    }
  }

  function setHour(newHour: number) {
    const minutesSeconds = format(newLocalTime.value, "m:s");
    setDate(parse(`${newHour}:${minutesSeconds}`, "H:m:s", newLocalTime.value));
  }

  function setMinute(newMinute: number) {
    const seconds = format(newLocalTime.value, "s");
    setDate(parse(`${newMinute}:${seconds}`, "m:s", newLocalTime.value));
  }

  function setSecond(newSecond: number) {
    setDate(parse(String(newSecond), "s", newLocalTime.value));
  }

  const time = computed<string>({
    get() {
      return format(newLocalTime.value, formatString.value);
    },
    set(newTime: string) {
      if (newTime.length !== formatString.value.length) {
        return;
      }

      const parsedDate = parse(newTime, formatString.value, localTime.value);

      setDate(parsedDate);
    },
  });

  const localHour = computed<number>(() =>
    Number(format(newLocalTime.value, "H"))
  );

  const localMinute = computed<number>(() =>
    Number(format(newLocalTime.value, "m"))
  );

  const localSecond = computed<number>(() =>
    Number(format(newLocalTime.value, "s"))
  );

  const hourOption = computed<TimeOption | undefined, TimeOption>({
    get() {
      return hourOptions.value.find(
        (option) => option.value === localHour.value
      );
    },
    set(newOption) {
      setHour(newOption.value);
    },
  });

  const minuteOption = computed<TimeOption | undefined, TimeOption>({
    get() {
      return minuteOptions.value.find(
        (option) => option.value === localMinute.value
      );
    },
    set(newOption) {
      setMinute(newOption.value);
    },
  });

  const secondOption = computed<TimeOption | undefined, TimeOption>({
    get() {
      return secondOptions.value.find(
        (option) => option.value === localSecond.value
      );
    },
    set(newOption) {
      setSecond(newOption.value);
    },
  });

  function getOptionTime(
    index: number,
    unit: TimeUnit,
    direction?: "after" | "before"
  ): Date {
    const hour = unit === "h" ? index : localHour.value;
    let minute = unit === "m" ? index : localMinute.value;
    let second = unit === "s" ? index : localSecond.value;

    if (direction) {
      if (unit === "h") {
        minute = direction === "after" ? 0 : 59;
        second = direction === "after" ? 0 : 59;
      }

      if (unit === "m") {
        second = direction === "after" ? 0 : 59;
      }
    }

    const hourMinuteSecond = `${hour}:${minute}:${second}`;

    return parse(hourMinuteSecond, "H:m:s", newLocalTime.value);
  }

  function isDisabled(index: number, unit: TimeUnit): boolean {
    if (localMinTime.value) {
      const optionTime = getOptionTime(index, unit, "before");

      if (isBefore(optionTime, localMinTime.value)) {
        return true;
      }
    }

    if (localMaxTime.value) {
      const optionTime = getOptionTime(index, unit, "after");

      if (isAfter(optionTime, localMaxTime.value)) {
        return true;
      }
    }

    if (interval.value) {
      let timestamp = getOptionTime(index, unit).getTime();

      if (localMinTime.value) {
        timestamp -= localMinTime.value.getTime();
      } else {
        timestamp -= localTime.value.getTime();
      }

      return timestamp % interval.value !== 0;
    }

    return false;
  }

  function setOptions(
    unit: TimeUnit,
    intervals: number,
    step = 1
  ): TimeOption[] {
    const options: TimeOption[] = [];

    for (let index = 0; index < intervals; index += 1) {
      let label: number | string = index;

      if (index < 10) {
        label = `0${label}`;
      }

      let show = true;

      if (step > 1 && index % step !== 0) {
        show = false;
      }

      if (show) {
        const disabled = isDisabled(index, unit);

        options.push({
          disabled,
          label,
          value: index,
        });
      }
    }

    return options;
  }

  function setPreviousLocalTime() {
    previousLocalTime.value = modelValue.value;
  }

  watch(modelValue, () => {
    pendingLocalTime.value = null;
  });

  watch(
    newLocalTime,
    () => {
      hourOptions.value = setOptions("h", 24);
      minuteOptions.value = setOptions("m", 60, minuteStep.value);
      secondOptions.value = setOptions("s", 60);
    },
    { immediate: true }
  );

  return {
    error,
    formatString,
    hourOption,
    hourOptions,
    localMaxTime,
    localMinTime,
    localTime,
    minuteOption,
    minuteOptions,

    newLocalTime,
    pendingLocalTime,
    previousLocalTime,
    secondOption,
    secondOptions,
    setHour,

    setMinute,
    setPreviousLocalTime,
    setSecond,
    time,
  };
}
